/* eslint-disable react/prop-types */
import * as Sentry from '@sentry/browser';
import { Badge, Button, Fab, Fade, FormControlLabel, Paper, Switch, Tab, Tabs, Typography, withStyles } from '@material-ui/core';
import { DRAWER_WIDTH_CLOSED, DRAWER_WIDTH_CLOSED_XS, DRAWER_WIDTH_OPEN } from 'styles/theme';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Formik, yupToFormErrors } from 'formik';
import { Prompt } from 'react-router-dom';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { isObject } from 'lodash';
import { withRouter } from 'react-router-dom';
import { withTheme } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import ReactRouterPropTypes from 'react-router-prop-types';
import classNames from 'classnames';
import queryString from 'query-string';

import { GET_CLIENT_DETAILS_FAILURE } from 'modules/clients/types';
import { GET_POLICY_SUMMARIES_BY_CLIENT_FAILURE } from 'modules/selfService/types';
import { GET_VENDORS_FAILURE } from 'modules/common/store/types';
import { GET_ALL_COUNTRIES_SUCCESS, GET_ALL_PROVINCES_SUCCESS, GET_ALL_ENUMERATIONS_FAILURE } from 'modules/enumerations/store/enumeationTypes';
import { InputListener } from 'modules/intake/form';
import { IntakeRecord } from 'modules/intake/models';
import { Permissions } from 'modules/layout/permissions';
import { TOAST_MESSAGE_SEVERITY_ERROR } from 'modules/layout/types';
import {
  clearIntakeRecordValidation,
  getTempIntakeFormDetails,
  setEEIntakeStatus,
  submitIntakeRecord,
  submitPncLead,
  submitPremiaLead,
  submitRocketLead,
  submitVendorLetter,
  validateIntakeRecord,
} from 'modules/intake/intake.actions';
import { formatDateGeneric, isEmptyDeep } from 'utilities/common';
import { getClientDetails } from 'modules/clients/clients.actions';
import { getPolicySummariesByClientId } from 'modules/selfService/selfService.actions';
import { getVendors } from 'modules/common/common.actions';
import { getVendors as getGlobalVendors, getVendorContacts } from 'modules/authorizations/authorizations.actions';
import { GET_VENDORS_FAILURE as GET_GLOBAL_VENDORS_FAILURE, GET_VENDOR_CONTACT_FAILURE } from 'modules/authorizations/types';
import { hasPermission } from 'modules/layout/permissionService';
import { hideToast, setPageTitle, showToast } from 'modules/layout/layout.actions';
import { getAllCountries, getAllProvinces, getAllEnumerations } from 'modules/enumerations/store/enumerationActions';
import ConfirmationDialog from 'modules/common/confirmationDialog.component';
import DeleteIntakeRecordDialog from 'modules/intake/summary/deleteIntakeRecordDialog.component';
import DownloadIntakePdfDialog from './downloadIntakePdfDialog.component';
import Log from 'utilities/log';
import NotesDrawer from 'modules/common/notesDrawer.component';
import Spinner from 'modules/common/spinner.component';
import SubmitIntakeDialog from './submitIntakeDialog.component';
import auth from 'auth/auth';

import {
  AUTHORIZATION_COMMENTS,
  AUTHORIZATION_INTAKE,
  BENEFITS_COUNSELING,
  HEADER_HEIGHT,
  HEADER_HEIGHT_SHRINK,
  INTAKE_RECORD_STATUS_CANCELLED,
  SUMMARY,
  TAB_ORDER,
  ValidationMode,
  VendorLetterDetails,
} from '../constants';
import {
  DELETE_INTAKE_RECORD_FAILURE,
  GET_INTAKE_FORM_METADATA_FAILURE,
  GET_INTAKE_RECORD_FAILURE,
  SUBMIT_INTAKE_RECORD_FAILURE,
  SUBMIT_INTAKE_RECORD_SUCCESS,
  SUBMIT_PNC_LEAD_FAILURE,
  SUBMIT_PREMIA_LEAD_FAILURE,
  SUBMIT_ROCKET_LEAD_FAILURE,
  SUBMIT_VENDOR_LETTERS_FAILURE,
  UPDATE_INTAKE_RECORD_FAILURE,
  UPDATE_INTAKE_RECORD_SUCCESS,
  VALIDATE_INTAKE_RECORD_FAILURE,
} from '../types';
import {
  clearFocusedGroup,
  deleteIntakeRecord,
  getIntakeFormMetadata,
  getIntakeRecord,
  setFocusedGroup,
  setFocusedTab,
  updateIntakeRecord,
} from '../intake.actions';
import { flattenIntakeRecordV2Response, mortgageLeadsAreValid, parsePncPermiaInfo, parseRocketInfo, remapMetadata, remapValidationResults } from '../utils';
import AuthorizationCommentsForm from './authorizationComments/authorizationCommentsForm.container';
import AuthorizationIntakeForm from './authorizationIntake/authorizationIntakeForm.component';
import BenefitsCounselingForm from './benefits/benefitsCounselingForm.component';
import IntakeRecordHeader from './intakeRecordHeader.component';
import Summary from './summary/summary.container';

const styles = (theme) => ({
  actionButtonContainer: {
    position: 'fixed',
    zIndex: theme.zIndex.drawer + 1,
    top: 0,
    right: 0,
    height: 64,
    display: 'flex',
    alignItems: 'center',
  },
  badgePadding: {
    padding: theme.spacing(0, 2),
  },
  button: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.common.white,
  },
  cancelIntakeButton: {
    color: theme.palette.error.main,
  },
  form: {
    backgroundColor: theme.palette.common.white,
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  invalidIcon: {
    marginLeft: theme.spacing(0.5),
  },
  navContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexDirection: 'row',
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(2),
  },
  savedMessage: {
    color: theme.palette.common.white,
  },
  tabsRoot: {
    paddingTop: HEADER_HEIGHT,
    paddingLeft: theme.spacing(20),
    paddingRight: theme.spacing(20),
    zIndex: 1,
  },
  shrinkTabs: {
    paddingTop: HEADER_HEIGHT_SHRINK,
  },
  tabContainer: {
    padding: theme.spacing(2),
    paddingTop: theme.spacing(23),
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  },
  tabContainerAlt: {
    padding: theme.spacing(2),
    paddingTop: theme.spacing(23),
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    backgroundColor: theme.palette.grey[50],
  },
  notesButtonContainer: {
    position: 'fixed',
    right: theme.spacing(.75),
    top: HEADER_HEIGHT - 80,
    zIndex: 1500,
    paddingTop: theme.spacing(8),
  },

  container: {
    height: 'auto',
    minHeight: HEADER_HEIGHT_SHRINK,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    zIndex: theme.zIndex.appBar,
    whiteSpace: 'nowrap',
    width: `calc(100% - ${DRAWER_WIDTH_CLOSED_XS}px)`,
    [theme.breakpoints.up('sm')]: {
      width: `calc(100% - ${DRAWER_WIDTH_CLOSED}px)`,
    },
  },
  drawerOpen: {
    width: `calc(100% - ${DRAWER_WIDTH_OPEN})`,
  },

});

function IntakeRecordDetailsView(props) {
  const [initialValues, setInitialValues] = useState(null);
  const [globalVendors, setGlobalVendors] = useState([]);
  const [globalVendorContacts, setGlobalVendorContacts] = useState([]);
  const [initialErrors, setInitialErrors] = useState(null);
  const [initialTouched, setInitialTouched] = useState(null);
  const [loadedIntakeRecord, setLoadedIntakeRecord] = useState(null);
  const [showSavedMessage, setShowSavedMessage] = useState(false);
  const [activeElement, setActiveElement] = useState(null);
  const [failedToLoadRecord, setFailedToLoadRecord] = useState(false);
  const [failedToLoadMetadata, setFailedToLoadMetadata] = useState(false);
  const [failedToSubmitRecord, setFailedToSubmitRecord] = useState(false);
  const [showSubmitConfirmationDialog, setShowSubmitConfirmationDialog] = useState(false);
  const [showDownloadPdfDialog, setShowDownloadPdfDialog] = useState(false);
  const [showSubmitSuccessfulDialog, setShowSubmitSuccessfulDialog] = useState(false);
  const [bankingInfoRequired, setBankingInfoRequired] = useState(true);
  const [reloadData, setReloadData] = useState(true);
  const [failedPrimaryValidation, setFailedPrimaryValidation] = useState(false);
  const [selfServicePolicySummaries, setSelfServicePolicySummaries] = useState(null);
  const [showNotesSideDrawer, setShowNotesSideDrawer] = useState(false);
  const [rocketLeadSent, setRocketLeadSent] = useState(false);
  const [pncLeadSent, setPncLeadSent] = useState(false);
  const [premiaLeadSent, setPremiaLeadSent] = useState(false);
  const [vendorLettersSent, setVendorLettersSent] = useState(false);
  const [isProcessingRecord, setIsProcessingRecord] = useState(false);
  const [finalSubmissionValues, setFinalSubmissionValues] = useState(null);
  const [showCancelIntakeDialog, setShowCancelIntakeDialog] = useState(false);
  const [isCancellingIntake, setIsCancellingIntake] = useState(false);

  const { location, setFocusedTab: propsSetFocusedTab, setPageTitle: propsSetPageTitle } = props;

  const formRef = useRef();

  //initial load of form
  useEffect(() => {
    propsSetPageTitle('Intake Record');

    const { tab } = queryString.parse(location.search);
    if (tab) {
      propsSetFocusedTab(tab);
    } else {
      propsSetFocusedTab(AUTHORIZATION_INTAKE);
    }
  }, [propsSetPageTitle, propsSetFocusedTab, location]);

  //reload data
  useEffect(() => {

    if (reloadData) {
      setReloadData(false);
      loadData();
    }
    async function loadData() {
      props.hideToast();

      const id = + props.match.params.id;

      setFailedToLoadRecord(false);
      let resp = await props.getIntakeRecord(id);
      if (resp.type === GET_INTAKE_RECORD_FAILURE) {
        props.showToast('Failed to retrieve data, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
        setFailedToLoadRecord(true);
        return;
      }

      // Transform the response directly (props.intakeRecords won't be updated until re-render)
      let intakeRecord = flattenIntakeRecordV2Response(resp.response);

      // Fetch client details separately using the V2 API
      // Try clientRecordId first (V2 system), fall back to clientId (legacy system)
      const clientLookupId = intakeRecord.clientRecordId || intakeRecord.clientId;

      if (clientLookupId) {
        const clientResp = await props.getClientDetails(clientLookupId);
        if (clientResp.type === GET_CLIENT_DETAILS_FAILURE) {
          Log.trace(`Failed to load client details for clientRecordId: ${intakeRecord.clientRecordId}, clientId: ${intakeRecord.clientId}`);
          props.showToast('Failed to retrieve client data, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
          setFailedToLoadRecord(true);
          return;
        }
        // Transform V2 client response to match expected format and attach to intakeRecord
        const clientData = clientResp.response;

        // Transform clientRelocationPolicies from V2 format to expected format
        // V2 API returns nested structure: { policy: { relocationPolicyId, policyCode, clientPolicyStatusId, ... }, policyDivisions: [], policyServices: [] }
        // Expected format: { id, policyCode, active, webViewable, isRepayAgreementRequired }
        const activeStatusId = '0f8bc13c-bc89-463c-9921-1288fb00edaa'; // Active status GUID from V2 API

        const transformedPolicies = (clientData.clientRelocationPolicies || []).map(policyWrapper => {
          // Extract the nested policy object
          const policy = policyWrapper.policy || policyWrapper;

          const transformed = {
            id: policy.relocationPolicyId || policy.RelocationPolicyId,
            policyCode: policy.policyCode || policy.PolicyCode,
            policyDescription: policy.policyDescription || policy.PolicyDescription,
            active: policy.clientPolicyStatusId === activeStatusId || policy.ClientPolicyStatusId === activeStatusId,
            webViewable: true, // V2 API doesn't have webViewable field, default to true
            isRepayAgreementRequired: Boolean(policy.isRepayAgreementRequired || policy.IsRepayAgreementRequired),
          };
          return transformed;
        });

        intakeRecord.client = {
          ...clientData.clientDetails,
          clientVendorRelationships: clientData.clientVendorRelationships || clientData.clientDetails.clientVendorRelationships || [],
          description: clientData.clientDetails.shortName, // Map shortName to description for intake form
          moveTrackClientId: clientData.clientDetails.clientId,
          relocationPolicyCodes: transformedPolicies,
          // Preserve database FK fields from IntakeRecord for saving
          clientRecordId: intakeRecord.clientRecordId,
          clientId: intakeRecord.clientId,
          policyId: intakeRecord.policyId,
          relocationCounselorId: intakeRecord.relocationCounselorId,
        };

        // Set relocationPolicyCodeId for backward compatibility with header component
        intakeRecord.relocationPolicyCodeId = intakeRecord.policyId;
      } else {
        // Log when neither client ID is available
        Log.trace(`Intake record ${intakeRecord.id} has no clientRecordId or clientId`);
        // Provide default client structure if no client ID is available
        // Still preserve any database ID fields that might exist
        intakeRecord.client = {
          relocationPolicyCodes: [],
          moveTrackClientId: null,
          clientRecordId: intakeRecord.clientRecordId || null,
          clientId: intakeRecord.clientId || null,
          policyId: intakeRecord.policyId || null,
          relocationCounselorId: intakeRecord.relocationCounselorId || null,
        };
      }

      let { metadata } = props;
      if (!metadata) {
        setFailedToLoadMetadata(false);
        let metadataResp = await props.getIntakeFormMetadata();
        if (metadataResp.type === GET_INTAKE_FORM_METADATA_FAILURE) {
          props.showToast('Failed to retrieve data, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
          setFailedToLoadMetadata(true);
          return;
        }
        metadata = remapMetadata(metadataResp.response);
      }

      // Load enumerations following the standard pattern
      let countries = props.countries || [];
      let provinces = props.provinces || [];

      // Check if enumerations need to be loaded
      if (props.enumerations.isLoaded === false || !props.enumerations.allEnums?.countries || !props.enumerations.allEnums?.provinces) {
        // First load base enumerations
        const allEnumsResp = await props.getAllEnumerations();

        if (allEnumsResp.type === GET_ALL_ENUMERATIONS_FAILURE) {
          props.showToast('Failed to retrieve enumerations, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
          setFailedToLoadMetadata(true);
          return;
        }

        // Then load countries and provinces in parallel
        const [countriesResp, provincesResp] = await Promise.all([
          props.getAllCountries(),
          props.getAllProvinces()
        ]);

        if (countriesResp.type === GET_ALL_COUNTRIES_SUCCESS) {
          countries = countriesResp.response || [];
        }
        if (provincesResp.type === GET_ALL_PROVINCES_SUCCESS) {
          provinces = provincesResp.response || [];
        }
      } else {
        // Use already loaded enumerations
        countries = props.enumerations.allEnums?.countries || [];
        provinces = props.enumerations.allEnums?.provinces || [];
      }

      // Partners (vendors on the API) feed the Area Tour Agent picker in the New Destination section.
      if (!props.partners || props.partners.length === 0) {
        const partnersResp = await props.getVendors();
        if (partnersResp.type === GET_VENDORS_FAILURE) {
          props.showToast('Failed to retrieve partners, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
        }
      }

      const [vendorsResponse, contactsResponse] = await Promise.all([
        props.getGlobalVendors(),
        props.getVendorContacts(),
      ]);
      if (vendorsResponse.type === GET_GLOBAL_VENDORS_FAILURE) {
        props.showToast('Failed to retrieve global service vendors, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
        setGlobalVendors([]);
      } else {
        setGlobalVendors(vendorsResponse.response || []);
      }
      if (contactsResponse.type === GET_VENDOR_CONTACT_FAILURE) {
        props.showToast('Failed to retrieve global service vendor contacts, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
        setGlobalVendorContacts([]);
      } else {
        setGlobalVendorContacts(contactsResponse.response || []);
      }

      if (auth.userHasPermission('selfService:read:policies') && intakeRecord.client.moveTrackClientId) {
        let policyResp = await props.getPolicySummariesByClientId(intakeRecord.client.moveTrackClientId);
        if (policyResp.type === GET_POLICY_SUMMARIES_BY_CLIENT_FAILURE) {
          props.showToast('Failed to retrieve data, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
          setFailedToLoadRecord(true);
          return;
        }

        setSelfServicePolicySummaries(policyResp.response);
      }
      else {
        setSelfServicePolicySummaries([]);
      }

      let vals = await IntakeRecord.fromWireData(intakeRecord, metadata, countries, provinces);

      setInitialValues(vals);
      setLoadedIntakeRecord(intakeRecord);

      if (intakeRecord.transfereeIntakeRequired) await props.getTempIntakeFormDetails(intakeRecord.id);
    }

  }, [reloadData, props]);

  const handleSectionFocus = (section) => {
    if (section) {
      handleTabChange(section.tab);
      props.setFocusedGroup(section.group);
    }
  };

  const handleTabChange = (tab) => {
    props.setFocusedTab(tab);

    const values = queryString.parse(props.location.search);
    values.tab = tab;
    props.history.replace(`${props.location.pathname}?${queryString.stringify(values)}`);
  };

  const handleChange = async (formikProps) => {
    const { metadata } = props;
    const isSubmitted = Boolean(
      formikProps.values?.primaryInformation?.intakeRecordStatusId &&
      formikProps.values.primaryInformation.intakeRecordStatusId !== 0 &&
      formikProps.values.primaryInformation.intakeRecordStatusId !== '0'
    );
    if (isSubmitted) return;
    if (formikProps.dirty && !formikProps.isSubmitting) {
      setActiveElement(document.activeElement);
      if (props.focusedTab !== SUMMARY) {
        formikProps.submitForm();
      }
      else {
        let resp = await save(formikProps.values);
        if (resp.type === UPDATE_INTAKE_RECORD_SUCCESS) {
          // Transform the response directly (props.intakeRecords won't be updated until re-render)
          const updatedRecord = flattenIntakeRecordV2Response(resp.response);
          // Re-attach client data to the updated record, including relocationPolicyCodes
          updatedRecord.client = loadedIntakeRecord?.client || {
            relocationPolicyCodes: loadedIntakeRecord?.client?.relocationPolicyCodes || [],
            moveTrackClientId: null,
            clientRecordId: updatedRecord.clientRecordId,
            clientId: updatedRecord.clientId,
            policyId: updatedRecord.policyId,
            relocationCounselorId: updatedRecord.relocationCounselorId,
          };
          const newValues = await IntakeRecord.fromWireData(updatedRecord, metadata, props.countries || [], props.provinces || []);
          setInitialValues(newValues);
          setLoadedIntakeRecord(updatedRecord);
        }
      }
    } else {
      Log.trace(
        `Not saving right now: props.dirty:${formikProps.dirty} props.isSubmitting: ${formikProps.isSubmitting} ${JSON.stringify(formikProps.errors)}`,
      );
      Log.trace(formikProps.values);
    }
  };

  const handleSaveRequest = async (values, { setSubmitting }) => {
    Log.trace(values, 'IntakeRecordDetails');
    const { metadata } = props;
    Sentry.setExtra('intakeRecord', values);

    // Never save a record that has already been submitted
    if (values?.primaryInformation?.intakeRecordStatusId &&
      values.primaryInformation.intakeRecordStatusId !== 0 &&
      values.primaryInformation.intakeRecordStatusId !== '0') {
      setSubmitting(false);
      return;
    }

    let resp = await save(values);
    if (resp.type === UPDATE_INTAKE_RECORD_SUCCESS) {
      // Transform the response directly (props.intakeRecords won't be updated until re-render)
      const updatedRecord = flattenIntakeRecordV2Response(resp.response);
      // Re-attach client data to the updated record, including relocationPolicyCodes
      updatedRecord.client = loadedIntakeRecord?.client || {
        relocationPolicyCodes: loadedIntakeRecord?.client?.relocationPolicyCodes || [],
        moveTrackClientId: null,
        clientRecordId: updatedRecord.clientRecordId,
        clientId: updatedRecord.clientId,
        policyId: updatedRecord.policyId,
        relocationCounselorId: updatedRecord.relocationCounselorId,
      };
      const newValues = await IntakeRecord.fromWireData(updatedRecord, metadata, props.countries || [], props.provinces || []);
      setInitialValues(newValues);
      setLoadedIntakeRecord(updatedRecord);
    }

    setSubmitting(false);
    return resp.type === UPDATE_INTAKE_RECORD_SUCCESS;
  };

  // Autosave Function
  const save = async (intakeRecordInstance) => {

    const { match } = props;
    Log.trace(intakeRecordInstance, 'Autosave');

    let showSavedMessageTimeout = null;

    clearTimeout(showSavedMessageTimeout);

    const edittedData = await intakeRecordInstance.toWireData();
    const id = +match.params.id;

    // Keep payload id aligned with the currently viewed intake route id.
    const payload = { ...edittedData, id };
    const resp = await props.updateIntakeRecord(payload);

    if (resp.type === UPDATE_INTAKE_RECORD_FAILURE) {
      props.showToast('Failed to save data, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
    } else {
      setShowSavedMessage(true);

      showSavedMessageTimeout = setTimeout(() => {
        setShowSavedMessage(false);
      }, 3000);

      if (activeElement) {
        setTimeout(() => activeElement.focus());
      }

      if (payload.transfereeIntakeRequired) await props.getTempIntakeFormDetails(id);
    }

    return resp;
  };

  const getCommentsLabel = (comments) => {
    const { classes } = props;
    const tabLabel = 'Authorization Comments';
    if (comments.length > 0) {
      return (
        <Badge className={classes.badgePadding} color="secondary" badgeContent="!">
          {tabLabel}
        </Badge>
      );
    }

    return tabLabel;
  };

  const changeTab = (offset) => {
    const { focusedTab } = props;
    const currentIndex = TAB_ORDER.indexOf(focusedTab);
    const tab = TAB_ORDER[currentIndex + offset];
    handleTabChange(tab);
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  };

  const validate = async (intakeRecord) => {
    const { metadata } = props;

    // validate against local schema before attempting to save data
    try {
      await IntakeRecord.schema.validate(intakeRecord, { abortEarly: false });
    } catch (err) {
      setFailedPrimaryValidation(true);
      return yupToFormErrors(err);
    }

    setFailedPrimaryValidation(false);


    // data needs to be saved for API to validate record
    const updateResponse = await save(intakeRecord);
    if (updateResponse.type === UPDATE_INTAKE_RECORD_FAILURE) {
      props.showToast('Failed to save data, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }

    const validateResponse = await props.validateIntakeRecord(intakeRecord);
    if (validateResponse.type === VALIDATE_INTAKE_RECORD_FAILURE) {
      props.showToast('Failed to validate record, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }

    const remappedResults = remapValidationResults(validateResponse.response);
    let errors = IntakeRecord.generateErrors(remappedResults);
    if (isEmptyDeep(errors)) errors = {};

    const updatedRecord = flattenIntakeRecordV2Response(updateResponse.response);
    // Re-attach client data to the updated record, including relocationPolicyCodes
    updatedRecord.client = loadedIntakeRecord?.client || {
      relocationPolicyCodes: loadedIntakeRecord?.client?.relocationPolicyCodes || [],
      moveTrackClientId: null,
      clientRecordId: updatedRecord.clientRecordId,
      clientId: updatedRecord.clientId,
      policyId: updatedRecord.policyId,
      relocationCounselorId: updatedRecord.relocationCounselorId,
    };
    const newValues = await IntakeRecord.fromWireData(updatedRecord, metadata, props.countries || [], props.provinces || []);
    setInitialValues(newValues);
    setLoadedIntakeRecord(updatedRecord);

    setInitialErrors(errors);

    const touchedFields = errorsToTouchedFields(errors);
    setInitialTouched(touchedFields);

    Log.trace(errors, 'AsyncValidator');

    return errors;
  };

  const errorsToTouchedFields = (obj) => {
    let result = {};
    for (let [key, value] of Object.entries(obj)) {
      if (isObject(value))
        result[key] = errorsToTouchedFields(value);
      else
        result[key] = value !== null;
    }

    return result;
  };

  const submit = async () => {
    setIsProcessingRecord(true);

    const updateResponse = await save(finalSubmissionValues);
    if (updateResponse.type === UPDATE_INTAKE_RECORD_FAILURE) {
      props.showToast('Failed to save data, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      setIsProcessingRecord(false);
      return;
    }

    const intakeRecord = {
      ...flattenIntakeRecordV2Response(updateResponse.response),
      client: loadedIntakeRecord?.client || {},
    };
    const selectedVendorLetters = finalSubmissionValues?.vendorLetters?.dynamicLetters || [];
    const brokerRequestLetterEnabled = !!finalSubmissionValues?.benefits?.homesaleProcess?.sendBrokerRequestLetter;
    const leadClientId = intakeRecord.clientRecordId || intakeRecord.clientId;
    const id = +props.match.params.id;

    const submitResponse = await props.submitIntakeRecord(id, false, bankingInfoRequired);
    if (submitResponse.type === SUBMIT_INTAKE_RECORD_FAILURE) {
      setShowSubmitConfirmationDialog(false);
      setFailedToSubmitRecord(false);
      props.showToast('Failed to submit record, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      setIsProcessingRecord(false);
      return;
    } else if (submitResponse.type === SUBMIT_INTAKE_RECORD_SUCCESS) {
      // Gather mortgage lead info
      if (intakeRecord && intakeRecord.moveTypeId === 1) {
        let intakeSummary = props.intakeSummaries?.find((o) => o.intakeRecordId === id);
        if (!leadClientId) {
          props.showToast('Unable to send mortgage leads or vendor letters: missing client record ID.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
        } else {
          const rocketRcInfo = intakeSummary?.relocationConsultant || { email: '' };

          // Submit Rocket Mortgage lead
          if (intakeRecord.mortgageLeadRocketSelected && !rocketLeadSent) {
            setRocketLeadSent(true);
            const parsedRocketInfo = parseRocketInfo(intakeRecord, rocketRcInfo);
            const submitRocketLeadResp = await props.submitRocketLead(parsedRocketInfo, leadClientId);
            if (submitRocketLeadResp.type === SUBMIT_ROCKET_LEAD_FAILURE) {
              props.showToast('Failed to send Rocket Mortgage lead.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
            }
          }

          // Get PNC and Premia info ready
          const parsedPncPermiaInfo = parsePncPermiaInfo(intakeRecord);

          // Submit PNC Mortgage lead
          if (intakeRecord.mortgageLeadPncSelected && !pncLeadSent) {
            setPncLeadSent(true);
            const submitPncLeadResp = await props.submitPncLead(parsedPncPermiaInfo, leadClientId);
            if (submitPncLeadResp.type === SUBMIT_PNC_LEAD_FAILURE) {
              props.showToast('Failed to send PNC mortgage lead.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
            }
          }

          // Submit Premia lead
          if (intakeRecord.mortgageLeadPremiaSelected && !premiaLeadSent) {
            setPremiaLeadSent(true);
            const submitPremiaResp = await props.submitPremiaLead(parsedPncPermiaInfo, leadClientId);
            if (submitPremiaResp.type === SUBMIT_PREMIA_LEAD_FAILURE) {
              props.showToast('Failed to send Premia mortgage lead.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
            }
          }

          if (brokerRequestLetterEnabled) {
            const vendorDetails = VendorLetterDetails.find((vd) => String(vd.id) === '126905');
            if (vendorDetails) {
              const submitBrokerRequestLetter = await props.submitVendorLetter(intakeRecord.mtAuthorizationId, '126905', vendorDetails.email, leadClientId, vendorDetails.templateName);
              if (submitBrokerRequestLetter.type === SUBMIT_VENDOR_LETTERS_FAILURE) {
                props.showToast('Failed to send Broker List request letter.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
              }
            }
          }
        }

        // Submit vendor letters
        if (leadClientId && selectedVendorLetters.length > 0 && !vendorLettersSent) {
          setVendorLettersSent(true);
          for (const selectedLetter of selectedVendorLetters) {
            const vendorId = String(selectedLetter?.id || selectedLetter);
            const vendorDetails = VendorLetterDetails.find((vd) => String(vd.id) === vendorId);
            if (!vendorDetails) {
              continue;
            }

            const submitVendorLetterResp = await props.submitVendorLetter(
              intakeRecord.mtAuthorizationId,
              vendorId,
              vendorDetails.email,
              leadClientId,
              vendorDetails.templateName,
            );

            if (submitVendorLetterResp.type === SUBMIT_VENDOR_LETTERS_FAILURE) {
              props.showToast('Failed to send one or more vendor letters.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
            }
          }
        }
      }

      // Disable the questionare if it is currently active
      if (intakeRecord.transfereeIntakeRequired) {
        props.setEEIntakeStatus(id, false);
      }

      setShowSubmitConfirmationDialog(false);
      setShowSubmitSuccessfulDialog(true);
    }
    setShowSubmitConfirmationDialog(false);
    setShowSubmitSuccessfulDialog(true);
    setIsProcessingRecord(false);
  };

  const handleChangeBankingInfoRequired = (event, value) => {
    setBankingInfoRequired(value);
  };

  const mortgageLeadInfoIsValid = (mortgageVendors, moveTypeId) => {
    if (!mortgageLeadsAreValid(mortgageVendors, moveTypeId)) {
      return false;
    } else {
      return true;
    }
  };

  const submitDisabled = (mortgageVendors, moveTypeId) => {
    if (initialValues) {
      if (initialValues.newDestination.isDestinationServiceInitiated && (initialValues.newDestination.isRenting || initialValues.newDestination.isPurchasing)) {
        if (moveTypeId === 7) {
          return false;
        }
        else if (mortgageLeadInfoIsValid(mortgageVendors, moveTypeId)) {
          return false;
        }
        else {
          return true;
        }
      } else {
        if (moveTypeId === 7) {
          return false;
        }
        else if (mortgageLeadInfoIsValid(mortgageVendors, moveTypeId)) {
          return false;
        }
        else {
          return true;
        }
      }
    }
    else {
      return true;
    }
  };

  // Soft delete: the record is marked Cancelled and stays visible (read-only) in the intake summary
  const handleCancelIntake = async () => {
    setShowCancelIntakeDialog(false);
    setIsCancellingIntake(true);
    const { type } = await props.deleteIntakeRecord({ intakeRecordId: +props.match.params.id });
    if (type === DELETE_INTAKE_RECORD_FAILURE) {
      setIsCancellingIntake(false);
      props.showToast('Failed to cancel intake, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }
    // isCancellingIntake stays set so the unsaved-changes prompt doesn't block leaving the cancelled record
    props.history.replace('/intakeRecords');
  };

  const isStrictValidation = props.validationMode === ValidationMode.Strict;
  // TODO: Add back submit permission checking when user permissions are enabled
  // const userCanSubmit = auth.userHasPermission('submit');
  const userCanSubmit = true;
  const userCanCancelIntake = hasPermission(Permissions.File.Intake.Cancel);

  if (failedToLoadRecord || failedToLoadMetadata || failedToSubmitRecord) {
    return (
      <div className="center-container">
        <Typography variant="h3" color="error" gutterBottom>
          Something went wrong
        </Typography>
        <Button size="large" onClick={() => { setReloadData(true); setInitialValues(null); setLoadedIntakeRecord(null); }} variant="contained" color="primary">
          Try Again
        </Button>
      </div>
    );
  }
  else {

    const intakeRecord = loadedIntakeRecord;
    const isReadOnly = Boolean(
      initialValues?.primaryInformation?.intakeRecordStatusId &&
      initialValues.primaryInformation.intakeRecordStatusId !== 0 &&
      initialValues.primaryInformation.intakeRecordStatusId !== '0'
    );
    const isCancelled = Number(initialValues?.primaryInformation?.intakeRecordStatusId) === INTAKE_RECORD_STATUS_CANCELLED;

    // Set when the user arrived here from an authorization (/authorizations/:id), so we can offer a way back.
    const { returnId } = queryString.parse(props.location.search);
    const returnAuthorizationId = Array.isArray(returnId) ? returnId[0] : returnId;

    return (

      (!initialValues || !selfServicePolicySummaries || !intakeRecord) ?
        <div className="center-container">
          <Spinner logo />
        </div>
        :
        <Fragment>
          <div className={props.classes.notesButtonContainer}>
            <Fab color="primary" onClick={() => { setShowNotesSideDrawer(true); }}>
              <div className="column align-center">
                <FontAwesomeIcon icon={['far', 'plus']} color={props.theme.palette.primary.contrastText} />
                Notes
              </div>
            </Fab>
          </div>

          <Formik
            initialValues={initialValues}
            initialErrors={initialErrors}
            initialTouched={initialTouched}
            validationSchema={isStrictValidation ? undefined : IntakeRecord.schema}
            validate={isStrictValidation ? validate : undefined}
            onSubmit={handleSaveRequest}
            enableReinitialize
            validateOnBlur={!isStrictValidation}
            validateOnChange={!isStrictValidation}
            innerRef={formRef}
          >
            {(formikProps) => (
              <form noValidate autoComplete="off" className={props.classes.form}>
                <Prompt
                  when={formikProps.dirty && !isReadOnly && !isCancellingIntake}
                  message={({ pathname }) => {
                    return pathname === props.location.pathname || 'You have unsaved changes, are you sure you want to leave?';
                  }}
                />
                <div className={props.classes.actionButtonContainer}>
                  {isReadOnly && (
                    <Typography className={props.classes.savedMessage} style={{ color: 'white', fontWeight: 600, marginRight: 8 }}>
                      {isCancelled
                        ? `Cancelled${intakeRecord.cancelledDate ? ` on ${formatDateGeneric(intakeRecord.cancelledDate)}` : ''} — View Only`
                        : 'Submitted — View Only'}
                    </Typography>
                  )}
                  <Fade in={showSavedMessage}>
                    <Typography className={props.classes.savedMessage}>Saved!</Typography>
                  </Fade>
                  {returnAuthorizationId && (
                    <Button
                      variant="contained"
                      className={props.classes.button}
                      onClick={() => props.history.push(`/authorizations/${returnAuthorizationId}`)}
                    >
                      Return to Authorization
                    </Button>
                  )}
                  {userCanCancelIntake && !isReadOnly && (
                    <Button
                      variant="contained"
                      className={classNames(props.classes.button, props.classes.cancelIntakeButton)}
                      disabled={formikProps.isSubmitting || isProcessingRecord || isCancellingIntake}
                      onClick={() => setShowCancelIntakeDialog(true)}
                    >
                      {isCancellingIntake ? <Spinner size="lg" color={props.theme.palette.error.main} /> : 'Cancel Intake'}
                    </Button>
                  )}
                  {!isReadOnly && (
                    <Button
                      variant="contained"
                      className={props.classes.button}
                      disabled={formikProps.isSubmitting || formikProps.isValidating || isProcessingRecord}
                      onClick={formikProps.submitForm}
                    >
                      {(formikProps.isSubmitting || isProcessingRecord) ? <Spinner size="lg" color="#fff" /> : 'Save'}
                      {!isEmptyDeep(formikProps.errors) && (
                        <FontAwesomeIcon
                          color={props.theme.palette.error.main}
                          icon={['fas', 'exclamation-circle']}
                          size="lg"
                          className={props.classes.invalidIcon}
                        />
                      )}
                    </Button>
                  )}
                </div>
                <InputListener onChange={isReadOnly ? () => { } : handleChange.bind(this, formikProps)} submitForm={formikProps.submitForm}>
                  <IntakeRecordHeader
                    {...formikProps}
                    eeIntakeForm={intakeRecord?.eeIntakeForm}
                    primaryInformation={formikProps.values.primaryInformation}
                    departureAddress={formikProps.values.departureProperty.address}
                  />
                  <Paper square className={classNames('position-fixed', props.classes.container, { [props.classes.drawerOpen]: false })}>
                    <Tabs
                      variant="fullWidth"
                      value={props.focusedTab}
                      onChange={(event, tab) => handleTabChange(tab)}
                      classes={{
                        root: classNames(props.classes.tabsRoot, { [props.classes.shrinkTabs]: !formikProps.values.primaryInformation.relocationDistance }),
                      }}
                    >
                      <Tab value={AUTHORIZATION_INTAKE} label="Authorization Intake" />
                      <Tab value={BENEFITS_COUNSELING} label="Benefits Counseling & Timeline" />
                      <Tab value={AUTHORIZATION_COMMENTS} label={getCommentsLabel(formikProps.values.authorizationComments)} />
                      <Tab value={SUMMARY} label="Summary" />
                    </Tabs>
                  </Paper>

                  <NotesDrawer
                    open={showNotesSideDrawer}
                    onClose={() => { setShowNotesSideDrawer(false); }}
                    notes={formikProps.values.notes}
                    saveNote={isReadOnly ? () => { } : (note) => {
                      formikProps.setFieldValue('notes', [...formikProps.values.notes, note], false);
                      // Wait until Formik acknowledges the update to form values, then trigger form submission
                      setTimeout(() => {
                        formikProps.submitForm();
                      }, 0);
                    }}
                    {...formikProps}
                  />
                  {props.focusedTab === AUTHORIZATION_INTAKE && (
                    <div className={props.classes.tabContainer}>
                      <fieldset disabled={isReadOnly} style={{ border: 'none', padding: 0, margin: 0 }}>
                        <AuthorizationIntakeForm
                          {...formikProps}
                          metadata={props.metadata}
                          focusedGroup={props.focusedGroup}
                          clearFocusedGroup={props.clearFocusedGroup}
                          selfServicePolicySummaries={selfServicePolicySummaries}
                          isReadOnly={isReadOnly}
                          partners={props.partners}
                          assignmentTypes={props.enumerations?.allEnums?.assignmentTypes || []}
                          enumerations={props.enumerations?.allEnums || {}}
                        />
                      </fieldset>
                    </div>
                  )}
                  {props.focusedTab === BENEFITS_COUNSELING && (
                    <div className={props.classes.tabContainer}>
                      <fieldset disabled={isReadOnly} style={{ border: 'none', padding: 0, margin: 0 }}>
                        <BenefitsCounselingForm
                          {...formikProps}
                          vendors={globalVendors}
                          vendorContacts={globalVendorContacts}
                          metadata={props.metadata}
                          enumerations={props.enumerations?.allEnums || {}}
                          isReadOnly={isReadOnly}
                          focusedGroup={props.focusedGroup}
                          onSectionFocus={handleSectionFocus}
                          clearFocusedGroup={props.clearFocusedGroup}
                        />
                      </fieldset>
                    </div>
                  )}
                  {props.focusedTab === AUTHORIZATION_COMMENTS && (
                    <div className={props.classes.tabContainerAlt}>
                      <fieldset disabled={isReadOnly} style={{ border: 'none', padding: 0, margin: 0 }}>
                        <AuthorizationCommentsForm
                          {...formikProps}
                          metadata={props.metadata}
                        />
                      </fieldset>
                    </div>
                  )}
                  {props.focusedTab === SUMMARY && (
                    <div className={props.classes.tabContainerAlt}>
                      <fieldset disabled={isReadOnly} style={{ border: 'none', padding: 0, margin: 0 }}>
                        <Summary
                          handleChange={isReadOnly ? () => { } : formikProps.handleChange}
                          handleBlur={isReadOnly ? () => { } : formikProps.handleBlur}
                          onSectionFocus={handleSectionFocus}
                          submitForm={isReadOnly ? () => { } : formikProps.submitForm}
                          isValid={formikProps.isValid && mortgageLeadInfoIsValid(formikProps.values.mortgageVendors, formikProps.values?.authorizationInformation?.moveTypeId)}
                          failedPrimaryValidation={failedPrimaryValidation}
                          isValidating={formikProps.isValidating || formikProps.isSubmitting || isProcessingRecord}
                          errors={formikProps.errors}
                          moveTypeId={formikProps.values?.authorizationInformation?.moveTypeId}
                          mortgageVendors={formikProps.values.mortgageVendors}
                          vendorLetters={formikProps.values.vendorLetters}
                          validationResults={props.validationResults}
                          hhgInitiated={initialValues.hhgInformation.isHhgServiceInitiated}
                          tempLivingInitiated={initialValues.temporaryLiving.isTemporaryLivingServiceInitiated}
                          newDestination={initialValues.newDestination}
                          brokerLetterSent={formikProps.values?.benefits?.homesaleProcess?.sendBrokerRequestLetter}
                        />
                      </fieldset>
                    </div>
                  )}
                </InputListener>
                <div className={props.classes.navContainer}>
                  <div>
                    <Button
                      className={props.classes.button}
                      variant="outlined"
                      size="large"
                      onClick={() => changeTab(-1)}
                      disabled={isReadOnly || TAB_ORDER.indexOf(props.focusedTab) === 0}
                    >
                      Previous
                    </Button>
                    <Button
                      className={props.classes.button}
                      variant="outlined"
                      size="large"
                      onClick={() => changeTab(1)}
                      disabled={isReadOnly || TAB_ORDER.indexOf(props.focusedTab) === TAB_ORDER.length - 1}
                    >
                      Next
                    </Button>
                  </div>
                  {props.focusedTab === SUMMARY && (
                    <Button
                      variant="outlined"
                      size="large"
                      color="primary"
                      onClick={() => {
                        setShowDownloadPdfDialog(true);
                      }}
                      disabled={isReadOnly || !formikProps.isValid || formikProps.isSubmitting || formikProps.isValidating || !initialValues}
                    >
                      Save as PDF
                    </Button>
                  )}
                  {props.focusedTab === SUMMARY && userCanSubmit && (
                    <div>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={bankingInfoRequired}
                            disabled={isReadOnly || !formikProps.isValid || formikProps.isSubmitting || formikProps.isValidating || !initialValues}
                            onChange={handleChangeBankingInfoRequired}
                            value={bankingInfoRequired}
                          />
                        }
                        label="Banking Info Required For Expenses"
                      />
                      {/* <FormControlLabel
                        control={
                          <Switch
                            checked={hasWebAccess}
                            disabled={!formikProps.isValid || formikProps.isSubmitting || formikProps.isValidating || !initialValues}
                            onChange={handleChangeHasWebAccess}
                            value={hasWebAccess}
                          />
                        }
                        label="Grant Web Access"
                      /> */}
                      <Button
                        variant="contained"
                        size="large"
                        color="primary"
                        onClick={() => { setShowSubmitConfirmationDialog(true); setFinalSubmissionValues(formikProps.values); }}
                        disabled={isReadOnly || !formikProps.isValid || formikProps.isSubmitting || isProcessingRecord || formikProps.isValidating || submitDisabled(formikProps.values.mortgageVendors, formikProps.values?.authorizationInformation?.moveTypeId)}
                      >
                        Submit
                      </Button>
                    </div>
                  )}
                </div>
              </form>
            )}
          </Formik>

          <SubmitIntakeDialog
            onSubmit={submit}
            onCancel={() => { setShowSubmitConfirmationDialog(false); }}
            isSubmitting={props.isSubmitting || isProcessingRecord}
            open={showSubmitConfirmationDialog}
          />

          <DeleteIntakeRecordDialog
            open={showCancelIntakeDialog}
            onCancel={() => { setShowCancelIntakeDialog(false); }}
            onSubmit={handleCancelIntake}
          />

          {showDownloadPdfDialog && (
            <DownloadIntakePdfDialog
              onCancel={() => { setShowDownloadPdfDialog(false); }}
              open={showDownloadPdfDialog}
              intakeRecordId={initialValues.id}
              authorizationId={initialValues.authorizationInformation.mtAuthorizationId}
            />
          )}

          <ConfirmationDialog
            onConfirm={() => {
              props.history.replace('/intakeRecords');
            }}
            open={showSubmitSuccessfulDialog}
            title="Record Submitted Successfully"
            confirmLabel="Ok"
            hideCancel={true}
            onCancel={() => { }}
          >
            <Typography>Your record was successfully submitted.</Typography>
          </ConfirmationDialog>
        </Fragment>
    );
  }

}

IntakeRecordDetailsView.propTypes = {
  classes: PropTypes.object.isRequired,
  setPageTitle: PropTypes.func.isRequired,
  history: ReactRouterPropTypes.history.isRequired,
  location: ReactRouterPropTypes.location.isRequired,
  match: ReactRouterPropTypes.match.isRequired,
  intakeRecords: PropTypes.object.isRequired,
  intakeSummaries: PropTypes.array,
  isLoading: PropTypes.bool.isRequired,
  getIntakeRecord: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired,
  hideToast: PropTypes.func.isRequired,
  getIntakeFormMetadata: PropTypes.func.isRequired,
  metadata: PropTypes.object,
  updateIntakeRecord: PropTypes.func.isRequired,
  setFocusedTab: PropTypes.func.isRequired,
  setFocusedGroup: PropTypes.func.isRequired,
  clearFocusedGroup: PropTypes.func.isRequired,
  focusedTab: PropTypes.string,
  focusedGroup: PropTypes.string,
  theme: PropTypes.object,
  clearIntakeRecordValidation: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  validationResults: PropTypes.array,
  validationMode: PropTypes.oneOf(Object.values(ValidationMode)),
  validateIntakeRecord: PropTypes.func.isRequired,
  submitIntakeRecord: PropTypes.func.isRequired,
  submitRocketLead: PropTypes.func.isRequired,
  submitPncLead: PropTypes.func.isRequired,
  submitPremiaLead: PropTypes.func.isRequired,
  deleteIntakeRecord: PropTypes.func.isRequired,
  partners: PropTypes.array,
  getVendors: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
  const {
    intakeRecords,
    intakeSummaries,
    isLoading,
    metadata,
    focusedTab,
    focusedGroup,
    validationResults,
    validationMode,
    isSubmitting,
  } = state.intake;

  const enumerations = state.enumerations;
  const countries = state.enumerations?.allEnums?.countries || [];
  const provinces = state.enumerations?.allEnums?.provinces || [];
  // Partners are stored as vendors on the common slice; the API still uses the vendor name.
  const { vendors: partners } = state.common;

  return {
    partners,
    intakeRecords,
    intakeSummaries,
    isLoading,
    metadata,
    focusedTab,
    focusedGroup,
    validationResults,
    validationMode,
    isSubmitting,
    enumerations,
    countries,
    provinces,
  };
};

export default compose(
  withStyles(styles),
  withRouter,
  withTheme,
  connect(mapStateToProps, {
    setPageTitle,
    getIntakeRecord,
    getTempIntakeFormDetails,
    showToast,
    hideToast,
    getIntakeFormMetadata,
    updateIntakeRecord,
    setFocusedTab,
    setFocusedGroup,
    clearFocusedGroup,
    clearIntakeRecordValidation,
    validateIntakeRecord,
    submitIntakeRecord,
    submitRocketLead,
    submitPremiaLead,
    submitPncLead,
    submitVendorLetter,
    deleteIntakeRecord,
    setEEIntakeStatus,
    getClientDetails,
    getPolicySummariesByClientId,
    getAllEnumerations,
    getAllCountries,
    getAllProvinces,
    getVendors,
    getVendorContacts,
    getGlobalVendors,
  }),
)(IntakeRecordDetailsView);